import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { AnnotationEditor } from "../components/annotation-editor";
import { LoadError, Loading, PageHeader } from "../components/page-state";
import { num, scaledStat } from "../lib/format";
import { enemyQuery, gameTableQuery } from "../lib/queries";

export const Route = createFileRoute("/monsters/$id")({ component: MonsterPage });

const ROLE_LABEL = { pool: "in waves", mini_boss: "mini-boss", boss: "boss" } as const;

function MonsterPage() {
	const { id } = Route.useParams();
	const enemy = useQuery(enemyQuery(id));
	const rules = useQuery(gameTableQuery("enemies"));

	if (enemy.isPending) return <Loading what="Loading monster" />;
	if (enemy.isError) return <LoadError error={enemy.error} retry={() => void enemy.refetch()} />;

	const e = enemy.data;
	const scaling = rules.data?.data as { stage_scaling_per_stage: number; chapter_multiplier: number; chapter_multipliers?: Record<string, number> } | undefined;
	const chapterIdx = [...new Set(e.roles.map((r) => r.chapter_index))].sort((a, b) => a - b);

	return (
		<div className="space-y-8">
			<PageHeader eyebrow={<Link to="/monsters">Monsters</Link>} title={e.name}>
				<code className="text-xs" style={{ color: "var(--color-ink-400)" }}>
					{e.id}
				</code>
			</PageHeader>

			<section className="grid gap-6 lg:grid-cols-[18rem_1fr]">
				<div className="panel p-5">
					<h2 className="mb-3 text-base">Base stats</h2>
					<dl className="grid grid-cols-2 gap-y-2 text-sm">
						<dt style={{ color: "var(--color-ink-300)" }}>HP</dt>
						<dd className="num text-right">{num(e.hp)}</dd>
						<dt style={{ color: "var(--color-ink-300)" }}>ATK</dt>
						<dd className="num text-right">{num(e.atk, 1)}</dd>
						<dt style={{ color: "var(--color-ink-300)" }}>DEF</dt>
						<dd className="num text-right">{num(e.def, 2)}</dd>
						<dt style={{ color: "var(--color-ink-300)" }}>XP per kill</dt>
						<dd className="num text-right">{e.xp}</dd>
						<dt style={{ color: "var(--color-ink-300)" }}>Coins per kill</dt>
						<dd className="num text-right">{e.coins}</dd>
						<dt style={{ color: "var(--color-ink-300)" }}>Crowd control</dt>
						<dd className="text-right">{e.cc_immune ? "Immune" : "Affected"}</dd>
					</dl>
					<p className="mt-4 text-xs" style={{ color: "var(--color-ink-400)" }}>
						Edit these in the game repo's data/enemies.json and re-import.
					</p>
				</div>

				<div className="panel p-5">
					<h2 className="mb-1 text-base">Where it appears</h2>
					{e.roles.length === 0 ? (
						<p className="text-sm" style={{ color: "var(--color-ink-400)" }}>
							No chapter uses this monster yet.
						</p>
					) : (
						<>
							<p className="mb-3 text-xs" style={{ color: "var(--color-ink-400)" }}>
								Stats at stage 1 and stage 20 of each chapter (before boss multipliers).
							</p>
							<table className="table">
								<thead>
									<tr>
										<th>Chapter</th>
										<th>Role</th>
										<th className="num">HP 1 → 20</th>
										<th className="num">ATK 1 → 20</th>
									</tr>
								</thead>
								<tbody>
									{chapterIdx.map((ci) => {
										const roles = e.roles.filter((r) => r.chapter_index === ci);
										const chId = roles[0]!.chapter_id;
										return (
											<tr key={ci}>
												<td>
													<Link to="/chapters/$id" params={{ id: chId }} className="underline decoration-[var(--color-ink-600)] underline-offset-2 hover:decoration-[var(--color-gold-400)]">
														Chapter {ci}
													</Link>
												</td>
												<td style={{ color: "var(--color-ink-300)" }}>{roles.map((r) => ROLE_LABEL[r.role]).join(", ")}</td>
												<td className="num">{scaling ? `${num(scaledStat(e.hp, ci, 1, scaling))} → ${num(scaledStat(e.hp, ci, 20, scaling))}` : "…"}</td>
												<td className="num">{scaling ? `${num(scaledStat(e.atk, ci, 1, scaling), 1)} → ${num(scaledStat(e.atk, ci, 20, scaling), 1)}` : "…"}</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</>
					)}
				</div>
			</section>

			<AnnotationEditor type="enemy" id={e.id} annotation={e.annotation} />
		</div>
	);
}
